import apiService from '../api/api.service';
import {
  FieldActivity,
  CreateFieldActivityDTO,
  UpdateFieldActivityDTO,
  CreateExpenseDTO,
  UpdateExpenseDTO,
  FieldActivityFilters,
  LocationLog,
  RouteResponse,
  PendingFieldActivity,
  ReviewFieldActivityDTO,
  AvailableVehiclesParams
} from '../../types/field.types';
import { Vehicle } from '../../types/vehicle.types';

class FieldActivityService {
  private static instance: FieldActivityService;

  public static getInstance(): FieldActivityService {
    if (!FieldActivityService.instance) {
      FieldActivityService.instance = new FieldActivityService();
    }
    return FieldActivityService.instance;
  }

  private constructor() {}

  // ==================== Saha Aktiviteleri ====================

  /**
   * Yeni saha aktivitesi oluşturur
   */
  public async createFieldActivity(data: CreateFieldActivityDTO): Promise<FieldActivity> {
    return apiService.post<FieldActivity>('/field-activities', data);
  }

  /**
   * Tüm saha aktivitelerini listeler (filtrelerle)
   */
  public async getFieldActivities(filters?: FieldActivityFilters): Promise<FieldActivity[]> {
    const params = new URLSearchParams();
    
    if (filters?.assignedToIds) params.append('assignedToIds', filters.assignedToIds);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.type) params.append('type', filters.type);

    const queryString = params.toString();
    const url = queryString ? `/field-activities?${queryString}` : '/field-activities';
    
    return apiService.get<FieldActivity[]>(url);
  }

  /**
   * Tek bir aktivitenin detaylarını getirir
   */
  public async getFieldActivityById(id: number): Promise<FieldActivity> {
    return apiService.get<FieldActivity>(`/field-activities/${id}`);
  }

  /**
   * Bir aktiviteyi günceller
   */
  public async updateFieldActivity(id: number, data: UpdateFieldActivityDTO): Promise<FieldActivity> {
    return apiService.put<FieldActivity>(`/field-activities/${id}`, data);
  }

  /**
   * Bir aktiviteyi siler
   */
  public async deleteFieldActivity(id: number): Promise<void> {
    return apiService.delete(`/field-activities/${id}`);
  }

  // ==================== Onay Süreci ====================

  /**
   * Mevcut kullanıcının karar verebileceği onay bekleyen aktiviteler (admin/manager)
   */
  public async getPendingApprovals(): Promise<PendingFieldActivity[]> {
    return apiService.get<PendingFieldActivity[]>('/field-activities/pending-approvals');
  }

  /**
   * Onay bekleyen aktivite talebini onaylar / reddeder (reddetmede neden zorunlu)
   */
  public async reviewFieldActivity(id: number, data: ReviewFieldActivityDTO): Promise<FieldActivity> {
    return apiService.put<FieldActivity>(`/field-activities/${id}/review`, data);
  }

  /**
   * Çalışanın verilen zaman aralığında kullanabileceği araçlar (zimmetli + boştaki havuz araçları)
   */
  public async getAvailableVehicles(params: AvailableVehiclesParams): Promise<Vehicle[]> {
    const query = new URLSearchParams();
    if (params.userId) query.append('userId', String(params.userId));
    query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    if (params.excludeActivityId) query.append('excludeActivityId', String(params.excludeActivityId));
    return apiService.get<Vehicle[]>(`/field-activities/available-vehicles?${query.toString()}`);
  }

  // ==================== Masraflar ====================

  /**
   * Aktiviteye masraf ekler
   */
  public async addExpense(activityId: number, data: CreateExpenseDTO): Promise<FieldActivity> {
    const formData = new FormData();
    
    formData.append('amount', data.amount.toString());
    formData.append('currency', data.currency);
    if (data.description) formData.append('description', data.description);
    formData.append('expenseDate', data.expenseDate);
    if (data.receipt) formData.append('receipt', data.receipt);

    return apiService.post<FieldActivity>(`/field-activities/${activityId}/expense`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Masraf kaydını günceller
   */
  public async updateExpense(expenseId: number, data: UpdateExpenseDTO): Promise<any> {
    const formData = new FormData();
    
    if (data.amount) formData.append('amount', data.amount.toString());
    if (data.currency) formData.append('currency', data.currency);
    if (data.description) formData.append('description', data.description);
    if (data.expenseDate) formData.append('expenseDate', data.expenseDate);
    if (data.receipt) formData.append('receipt', data.receipt);

    return apiService.put(`/field-activities/expense/${expenseId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Masraf kaydını siler
   */
  public async deleteExpense(expenseId: number): Promise<void> {
    return apiService.delete(`/field-activities/expense/${expenseId}`);
  }

  // ==================== Dosya Ekleri ====================

  /**
   * Aktiviteye dosya ekler
   */
  public async addAttachment(activityId: number, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    return apiService.post(`/field-activities/${activityId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Masraf fişini görüntüler/indirir
   */
  public async getReceipt(fileName: string): Promise<Blob> {
    return apiService.get(`/field-activities/receipts/${fileName}`, {
      responseType: 'blob',
    });
  }

  // ==================== Konum Takibi ====================

  /**
   * Kullanıcının belirli bir tarihteki konum rotasını ve aktivitelerini getirir
   */
  public async getUserLocationRoute(userId: number, date: string): Promise<RouteResponse> {
    return apiService.get<RouteResponse>(`/location-logs/route/${userId}?date=${date}`);
  }

  /**
   * Belirli bir aktivitenin konum geçmişini getirir
   */
  public async getActivityLocationHistory(fieldActivityId: number): Promise<LocationLog[]> {
    return apiService.post<LocationLog[]>('/field-activities/location-history', {
      fieldActivityId
    });
  }
}

export default FieldActivityService.getInstance();
